import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import '../../styles/CustomSelect.css';

/**
 * Generic custom dropdown select component
 * 
 * @param {Array} options - Array of options: [{ value: string, label: string, icon?: string|ReactNode, badge?: ReactNode, ...customProps }]
 * @param {string} value - Currently selected value
 * @param {function} onChange - Called with { target: { value, name } } to match native select API
 * @param {string} name - Form field name
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {string} className - Additional CSS classes
 * @param {string} preferDirection - Preferred dropdown direction: 'down' or 'up' (default: 'auto')
 * @param {function} renderOption - Custom render function for dropdown options: (option) => ReactNode
 * @param {function} renderSelected - Custom render function for selected value: (option) => ReactNode
 */
function CustomSelect({ 
  options = [], 
  value, 
  onChange, 
  name,
  disabled = false, 
  placeholder = 'Select...',
  className = '',
  preferDirection = 'auto',
  minDropdownWidth = null,
  renderOption,
  renderSelected
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Calculate dropdown position
  const updateDropdownPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const optionsCount = options.length + (placeholder ? 1 : 0);
      const estimatedDropdownHeight = Math.min(optionsCount * 48, 250); // ~48px per option
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top;
      let openDirection = 'down';
      
      // Determine direction based on preference and available space
      if (preferDirection === 'down') {
        // Force down
        openDirection = 'down';
        top = rect.bottom;
      } else if (preferDirection === 'up') {
        // Force up
        openDirection = 'up';
        top = rect.top - estimatedDropdownHeight;
      } else {
        // Auto: prefer down unless very limited space (less than estimated height needed)
        // and there's significantly more space above
        if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow * 2) {
          openDirection = 'up';
          top = rect.top - Math.min(estimatedDropdownHeight, spaceAbove - 10);
        } else {
          openDirection = 'down';
          top = rect.bottom;
        }
      }
      
      setDropdownStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        width: minDropdownWidth ? `${Math.max(rect.width, minDropdownWidth)}px` : `${rect.width}px`,
        maxHeight: openDirection === 'up' 
          ? `${Math.min(250, spaceAbove - 10)}px`
          : `${Math.min(250, spaceBelow - 10)}px`,
        openDirection
      });
    }
  }, [options.length, placeholder, preferDirection, minDropdownWidth]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Also check if click is on the dropdown portal
        const dropdownEl = document.querySelector('.custom-select-dropdown-portal');
        if (dropdownEl && dropdownEl.contains(event.target)) {
          return;
        }
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Update position on scroll or resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      const handleScrollOrResize = () => {
        updateDropdownPosition();
      };
      
      // Listen for scroll on window and all scrollable parents
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      
      // Also listen for scroll on any modal body or scrollable container
      const modalBody = triggerRef.current?.closest('.modal-body');
      if (modalBody) {
        modalBody.addEventListener('scroll', handleScrollOrResize);
      }
      
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
        if (modalBody) {
          modalBody.removeEventListener('scroll', handleScrollOrResize);
        }
      };
    }
  }, [isOpen, updateDropdownPosition]);

  const selectedOption = options.find(o => o.value === value || o.value?.toString() === value?.toString());

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        updateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue, name } });
    setIsOpen(false);
  };

  // Default render for option content
  const defaultRenderOptionContent = (option, isPlaceholder = false) => {
    if (isPlaceholder) {
      return <span className="custom-select-placeholder">{placeholder}</span>;
    }

    return (
      <>
        {option.icon && (
          <span className="custom-select-icon">
            {typeof option.icon === 'string' ? option.icon : option.icon}
          </span>
        )}
        <span className="custom-select-label">{option.label}</span>
        {option.badge && (
          <span className="custom-select-badge">{option.badge}</span>
        )}
      </>
    );
  };

  // Use custom renderers if provided, otherwise use default
  const renderOptionContent = (option, isPlaceholder = false) => {
    if (isPlaceholder) {
      return <span className="custom-select-placeholder">{placeholder}</span>;
    }
    if (renderOption) {
      return renderOption(option);
    }
    return defaultRenderOptionContent(option);
  };

  const renderSelectedContent = (option) => {
    if (!option) {
      return <span className="custom-select-placeholder">{placeholder}</span>;
    }
    if (renderSelected) {
      return renderSelected(option);
    }
    return defaultRenderOptionContent(option);
  };

  const dropdownContent = isOpen && (
    <div 
      className={`custom-select-dropdown-portal ${dropdownStyle.openDirection === 'up' ? 'open-up' : 'open-down'}`}
      style={{
        position: dropdownStyle.position,
        top: dropdownStyle.top,
        left: dropdownStyle.left,
        width: dropdownStyle.width,
        maxHeight: dropdownStyle.maxHeight,
        zIndex: 99999
      }}
    >
      {placeholder && (
        <div
          className={`custom-select-option ${!value ? 'selected' : ''}`}
          onClick={() => handleSelect('')}
        >
          <span className="custom-select-placeholder">{placeholder}</span>
        </div>
      )}
      {options.map((option, index) => (
        <div
          key={option.value ?? index}
          className={`custom-select-option ${value?.toString() === option.value?.toString() ? 'selected' : ''}`}
          onClick={() => handleSelect(option.value)}
        >
          {renderOptionContent(option)}
        </div>
      ))}
    </div>
  );

  return (
    <div className={`custom-select ${disabled ? 'disabled' : ''} ${className}`} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="custom-select-value">
          {renderSelectedContent(selectedOption)}
        </span>
        <ChevronDown size={18} className={`custom-select-arrow ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
}

export default CustomSelect;
